import React, { useState, useMemo, useEffect } from "react";
import "../../styles/main.css";
import theme from "../../styles/theme";

import TabsBase from "../../components/reusable/Navigation/TabsBase";
import ContainerBase from "../../components/framepart/SubpageLayout/ContainerBase";
import ToolbarHeadSubpage from "../../components/framepart/SubpageLayout/ToolbarHeadSubpage";

import DropdownMulti from "../../components/reusable/Forms/DropdownMulti";
import DropdownList from "../../components/reusable/Forms/DropdownList";
import TextField from "../../components/reusable/Forms/TextField";
import TextArea from "../../components/reusable/Forms/TextArea";
import NumberStepField from "../../components/reusable/Forms/NumberStepField";

import Button from "../../components/reusable/Forms/Button";
import Switch from "../../components/reusable/Forms/Switch";
import SwitchButton from "../../components/reusable/Forms/SwitchButton";
import IconButton from "../../components/reusable/Forms/IconButton";

import RadioGroup from "../../components/reusable/Forms/RadioGroup";
import CheckboxGroup from "../../components/reusable/Forms/CheckboxGroup";
import SliderSelector from "../../components/reusable/Forms/SliderSelector";
import StepSelector from "../../components/reusable/Forms/StepSelector";
import MockEngineViewer from "../../components/advanced/ConfigEngine/mockEngineViewer";

import JourneyTabs from "../../components/reusable/Navigation/JourneyTabs";

/* ============================================================================
   OPTIONS (référentiels UI / listes)
============================================================================ */

import {
  countryOptions,
  SECTOR_OPTIONS,
  TYPOLOGY_OPTIONS,
  SUB_TYPOLOGY_OPTIONS,
  PROJECT_PHASE_OPTIONS,
  OPERATIONAL_CONTINUITY_OPTIONS,
  DELIVERY_METHOD_OPTIONS,
  PROJECT_GOVERNANCE_LEVEL_OPTIONS,
  STANDARDS_OPTIONS,
  DISCIPLINES_OPTIONS,
  PROJECT_FOOTPRINT_OPTIONS,
  INTERVENTION_NATURE_OPTIONS,
  FUNCTIONAL_ORGANIZATION_OPTIONS,
  DESIGN_MATURITY_OPTIONS,
  BIM_MATURITY_OPTIONS,
  COMPLEXITY_LEVEL_OPTIONS,
  SITE_CONSTRAINTS_OPTIONS,
  INTERFACES_OPTIONS,
  SUBSYSTEM_DECOMPOSITION_OPTIONS,

  SYSTEM_ORGANIZATION_OPTIONS,
  INTERCONNECTION_TOPOLOGY_OPTIONS,
  MODULARITY_APPROACH_OPTIONS,
  EXPANSION_STRATEGY_OPTIONS,

  FUNCTIONAL_ZONES_OPTIONS,
  

  OPERATING_MODE_OPTIONS,
  SERVICE_AVAILABILITY_OPTIONS,
  BUSINESS_CRITICALITY_OPTIONS,
  SERVICE_CONTINUITY_OPTIONS,
  LAYOUT_STRATEGY_OPTIONS,  
  INTEGRATION_LEVEL_OPTIONS,
  MAIN_FLOWS_OPTIONS,
  EXTERNAL_CONNECTIONS_OPTIONS,
  AUTOMATION_LEVEL_OPTIONS,
  DATA_MONITORING_IMPORTANCE_OPTIONS,
  SAFETY_CLASSIFICATION_OPTIONS,
  REGULATORY_ENVIRONMENT_OPTIONS,
  AVAILABILITY_TARGET_OPTIONS,
  DESIGN_ROBUSTNESS_OPTIONS,
} from "../../taxonomies/index-scope";


/* ============================================================================
   STYLES (containers + helpers)
============================================================================ */
const baseZoneStyle = {
  minHeight: "calc(100vh - 290px)",
  padding: 12,
  borderRadius: 6,
  background: theme.colors.background.surface,
  border: `1px solid ${theme.colors.borders.default}`,
  overflowX: "auto",
  overflowY: "auto",
  flexShrink: 0,
};

const baseLeftZoneStyle = { ...baseZoneStyle, flex: 3, minWidth: 700 };
const baseRightZoneStyle = { ...baseZoneStyle, flex: 1, minWidth: 350, boxSizing: "border-box" };

const infoTextStyle = { fontFamily: theme.fonts.body, fontSize: theme.fontSizes.sm, color: theme.colors.text.body, lineHeight: 1.4 };
const infoListStyle = { paddingLeft: 18, margin: 0 };
const infoParagraphStyle = { marginTop: 0, marginBottom: 8 };

const grid2ColsStyle = { display: "grid", gridTemplateColumns: "1fr 1fr", gridAutoRows: "min-content", columnGap: 80, rowGap: 18, alignItems: "start" };
const grid3ColsStyle = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gridAutoRows: "min-content", columnGap: 20, rowGap: 18, alignItems: "start" };

// Carte "horizontale" : Titre au-dessus + 3 colonnes en dessous
const cardRow3Style = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: 14,
  borderRadius: 8,
  background: theme.colors.background.surface,
  boxSizing: "border-box",
  minHeight: 180,
};

const cardBody3ColsStyle = {
  display: "grid",
  gridTemplateColumns: "550px 525px 550px",
  columnGap: 20,
  rowGap: 25,
  alignItems: "start",
};


const cardStyle = { display: "flex", flexDirection: "column", gap: 16, padding: 14, borderRadius: 8, background: theme.colors.background.surface, boxSizing: "border-box", minHeight: 180 };
const cardStyleH = { display: "flex", flexDirection: "row", gap: 16, padding: 14, borderRadius: 8, background: theme.colors.background.surface, boxSizing: "border-box", minHeight: 180 };
const cardTitleStyle = { fontSize: theme.fontSizes.md, fontWeight: theme.fontWeights.normal, width: "100%", whiteSpace: "normal",color: theme.colors.text.caneva, paddingBottom: 8, borderBottom: `1px solid ${theme.colors.borders.default}` };

const dropdownBaseProps = { width: "100%", height: 32, gap: 10, padding: "3px 6px" };

/* ============================================================================
   HELPERS (CheckboxGroup)
============================================================================ */
const toggleMultiValue = (prev, value, checked) => {
  if (checked) return Array.from(new Set([...(prev ?? []), value]));
  return (prev ?? []).filter((v) => v !== value);
};

/* ============================================================================
   PAGE
============================================================================ */
const Subpage_101 = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [switchOn, setSwitchOn] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const [toolbarCategories, setToolbarCategories] = useState([]);
  const [isCoreLensOpen, setIsCoreLensOpen] = useState(false);
  useEffect(() => setIsCoreLensOpen(false), [activeTab]);

  /* ------------------------------ */
  /* Identity — État local          */
  /* ------------------------------ */
  const [identity, setIdentity] = useState({
    projectName: "",
    projectCode: "",
    description: "",
    projectOwner: "",
    deliveryMethod: "",
    projectGovernance: "",
    contractReference: "",
    duration: "",
    city: "",
    country: "",
  });

  const updateIdentityField = (field, value) => setIdentity((prev) => ({ ...prev, [field]: value }));
  
  /* Identity — Options (listes) */
const deliveryMethodOptions = DELIVERY_METHOD_OPTIONS;
const projectGovernanceOptions = PROJECT_GOVERNANCE_LEVEL_OPTIONS;

/* Identity — Champ dérivé (confort) */
const durationWeeks = identity.duration ?? "";

  /* ------------------------------ */
  /* Typology — État local          */
  /* ------------------------------ */
  const [sector, setSector] = useState("");
  const [typology, setTypology] = useState("");
  const [subTypology, setSubTypology] = useState([]);

  const [projectFootprint, setProjectFootprint] = useState([]);
  const [interventionNature, setInterventionNature] = useState([]);
  const [operationalContinuity, setOperationalContinuity] = useState("");
  const [siteConstraints, setSiteConstraints] = useState([]);
  const [phasedExecution, setPhasedExecution] = useState(false);


  // (STATE) — Typology / Functional configuration

  const [functionalZones, setFunctionalZones] = useState([]);
  
  const [temporaryWorks, setTemporaryWorks] = useState(false);
  const [segmentedLayout, setSegmentedLayout] = useState(false);

  const [subsystemDecomposition, setSubsystemDecomposition] = useState("");

  const [complexityLevel, setComplexityLevel] = useState("");
  const [designMaturity, setDesignMaturity] = useState("");
  const [disciplines, setDisciplines] = useState([]);

  const [systemsOrganization, setSystemsOrganization] = useState("");
  const [interconnectionTopology, setInterconnectionTopology] = useState("");
  const [modularityApproach, setModularityApproach] = useState("");
  const [expansionStrategy, setExpansionStrategy] = useState("");



  const [projectPhase, setProjectPhase] = useState([]);
  const [standards, setStandards] = useState([]);
  const [bimMaturity, setBimMaturity] = useState("");
  const [interfaces, setInterfaces] = useState([]);
  const [functionalOrganization, setFunctionalOrganization] = useState([]);

  const sectorOptions = SECTOR_OPTIONS;
  const handleSectorChange = (v) => { setSector(v); setTypology(""); setSubTypology([]); };
  const typologyOptionsFiltered = useMemo(() => (!sector ? [] : TYPOLOGY_OPTIONS.filter((t) => t.parent === sector).map(({ value, label }) => ({ value, label }))), [sector]);
  const subTypologyOptionsFiltered = useMemo(() => (!typology ? [] : SUB_TYPOLOGY_OPTIONS.filter((s) => s.parent === typology).map(({ value, label }) => ({ value, label }))), [typology]);
  const isSubTypologyEnabled = useMemo(() => subTypologyOptionsFiltered.length > 0, [subTypologyOptionsFiltered]);
  useEffect(() => { if (!isSubTypologyEnabled && (subTypology?.length ?? 0) > 0) setSubTypology([]); }, [isSubTypologyEnabled]);
  
  const projectFootprintOptions = PROJECT_FOOTPRINT_OPTIONS;
  const interventionNatureOptions = INTERVENTION_NATURE_OPTIONS;
  const operationalContinuityOptions = OPERATIONAL_CONTINUITY_OPTIONS;
  const siteConstraintsOptions = SITE_CONSTRAINTS_OPTIONS;
 
  const systemsOrganizationOptions = SYSTEM_ORGANIZATION_OPTIONS;
  const interconnectionTopologyOptions = INTERCONNECTION_TOPOLOGY_OPTIONS;
  const modularityApproachOptions = MODULARITY_APPROACH_OPTIONS;
  const expansionStrategyOptions = EXPANSION_STRATEGY_OPTIONS;


  const functionalZonesOptions = FUNCTIONAL_ZONES_OPTIONS;

  const complexityLevelOptions = COMPLEXITY_LEVEL_OPTIONS;
  const designMaturityOptions = DESIGN_MATURITY_OPTIONS;

/* ============================================================================
     COMPONENTS — STATE
  ============================================================================ */
  // 1) STATE
  const [mainFlows, setMainFlows] = useState([]);
  const [externalConnections, setExternalConnections] = useState([]);


  // 2) CONST (options importées)
  const mainFlowsOptions = MAIN_FLOWS_OPTIONS;
  const externalConnectionsOptions = EXTERNAL_CONNECTIONS_OPTIONS;
  const disciplinesOptions = DISCIPLINES_OPTIONS;


  // 3) OPTIONS ADAPTÉES AU CheckboxGroup (avec checked)
  const mainFlowsCheckboxOptions = useMemo(
    () => mainFlowsOptions.map((o) => ({ ...o, checked: mainFlows.includes(o.value) })),
    [mainFlows, mainFlowsOptions]
  );

  const externalConnectionsCheckboxOptions = useMemo(
    () => externalConnectionsOptions.map((o) => ({ ...o, checked: externalConnections.includes(o.value) })),
    [externalConnections, externalConnectionsOptions]
  );

  const disciplinesCheckboxOptions = useMemo(
    () => disciplinesOptions.map((o) => ({ ...o, checked: disciplines.includes(o.value) })),
    [disciplines, disciplinesOptions]
  );

  // 4) HANDLERS
  const handleMainFlowsChange = (value, state) => {
    setMainFlows((prev) =>
      state ? [...prev, value] : prev.filter((v) => v !== value)
    );
  };

  const handleExternalConnectionsChange = (value, state) => {
    setExternalConnections((prev) =>
      state ? [...prev, value] : prev.filter((v) => v !== value)
    );
  };

  const handleDisciplinesChange = (value, state) => {
    setDisciplines((prev) =>
      state ? [...prev, value] : prev.filter((v) => v !== value)
    );
  };



  // 4) MARKS (à partir des options importées : value/label)
  const complexityLevelMarks = useMemo(() => complexityLevelOptions.map((o) => ({ value: Number(o.value), label: o.label })), [complexityLevelOptions]);
  const designMaturityMarks = useMemo(() => designMaturityOptions.map((o) => ({ value: Number(o.value), label: o.label })), [designMaturityOptions]);


  const functionalOrganizationOptions = FUNCTIONAL_ORGANIZATION_OPTIONS; 
  const standardsOptions = STANDARDS_OPTIONS;
  
  const bimMaturityOptions = BIM_MATURITY_OPTIONS;  
  const interfacesOptions = INTERFACES_OPTIONS;
  const projectPhaseOptions = PROJECT_PHASE_OPTIONS; 



  /* ============================================================================
     CONCEPTUAL ARCHITECTURE — STATE
  ============================================================================ */
  const [projectOperatingMode, setProjectOperatingMode] = useState("");
  const [serviceAvailabilityRequirement, setServiceAvailabilityRequirement] = useState("");
  const [businessCriticality, setBusinessCriticality] = useState("");
  const [serviceContinuityStrategy, setServiceContinuityStrategy] = useState("");

  const [overallLayoutStrategy, setOverallLayoutStrategy] = useState("");
  
  const [futureExpansionStrategy, setFutureExpansionStrategy] = useState("");
  const [systemIntegrationLevel, setSystemIntegrationLevel] = useState("");

  const [mainOperationalFlows, setMainOperationalFlows] = useState([]);
  const [externalSystemConnections, setExternalSystemConnections] = useState([]);
  const [automationLevel, setAutomationLevel] = useState("");
  const [dataMonitoringImportance, setDataMonitoringImportance] = useState("");

  const [safetyClassification, setSafetyClassification] = useState("");
  const [regulatoryEnvironment, setRegulatoryEnvironment] = useState("");
  const [availabilityTarget, setAvailabilityTarget] = useState("");
  const [designRobustnessLevel, setDesignRobustnessLevel] = useState("");

  const mainOperationalFlowsCheckboxOptions = useMemo(
    () =>
      MAIN_FLOWS_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
        checked: mainOperationalFlows.includes(o.value),
      })),
    [mainOperationalFlows]
  );
  const externalSystemConnectionsCheckboxOptions = useMemo(
    () =>
      EXTERNAL_CONNECTIONS_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
        checked: externalSystemConnections.includes(o.value),
      })),
    [externalSystemConnections]
  );


  

const [typologyConfig, setTypologyConfig] = useState("TYP-BLD-01");
const [start, setStart] = useState("2026-01-01");







  /* ------------------------------ */
  /* Constraints — État local       */
  /* ------------------------------ */
  const [constraints, setConstraints] = useState({});
  const updateConstraintsField = (field, value) => setConstraints((prev) => ({ ...prev, [field]: value }));

  /* ------------------------------ */
  /* Objectives and Risks — État local */
  /* ------------------------------ */
  const [objectives, setObjectives] = useState({});
  const updateObjectivesField = (field, value) => setObjectives((prev) => ({ ...prev, [field]: value }));

  /* ============================================================================
     ZONE A1 & A2 — IDENTITY
  ============================================================================ */
  const zoneA1 = {
    style: baseLeftZoneStyle,
    gap: 12,
    sectionGap: 12,
    sections: [
      {
        content: (() => {
          const identityGridStyle = grid2ColsStyle;

          const identityCardStyle = { ...cardStyle, gap: 14, paddingRight: 80 };
          const identityCardTitleStyle = { fontSize: theme.fontSizes.md, fontWeight: theme.fontWeights.normal, color: theme.colors.text.caneva, paddingBottom: 8, borderBottom: `1px solid ${theme.colors.borders.default}` };

          const fieldProps = { width: "100%", height: 32, labelWidth: 150, gap: 12, padding: "3px 6px", marginTop: 0, marginBottom: 0 };
          const compactTitleStyle = { fontSize: theme.fontSizes.md, fontWeight: theme.fontWeights.normal, color: theme.colors.text.caneva, width: 260, marginLeft: 0, borderBottom: `1px solid ${theme.colors.borders.default}`, paddingBottom: 8 };

          return (
            <div style={identityGridStyle}>
              {/* (1) PROJECT IDENTITY */}
              <div style={identityCardStyle}>
                <div style={identityCardTitleStyle}>Project identity</div>
                  <TextField label="Project Code" required placeholder="Ex: AEP-2026-01" value={identity.projectCode} onChange={(e) => updateIdentityField("projectCode", e.target.value.slice(0, 50))} {...fieldProps} />
                  <TextArea label="Project Name" required placeholder="Ex: WENCHI WTP" rows={2} resize="none" value={identity.projectName} onChange={(e) => updateIdentityField("projectName", e.target.value)} labelWidth={150} gap={12} />
                  <TextArea label="Description" placeholder="Ex: Projet d'approvisionnement en eau potable de la ville de Wenchi" rows={2} resize="none" value={identity.description} onChange={(e) => updateIdentityField("description", e.target.value)} labelWidth={150} gap={12} />
                  <TextArea label="Project Owner" placeholder="Ex: GhanaWater Ltd" rows={2} resize="none" value={identity.projectOwner} onChange={(e) => updateIdentityField("projectOwner", e.target.value)} labelWidth={150} gap={12} />
                </div>

              {/* (2) CONTRACTUAL */}
              <div style={identityCardStyle}>
                <div style={identityCardTitleStyle}>Contractual</div>
                <TextField label="Contract Reference" placeholder="Ex: AO-2026-014 / Contract C-245" value={identity.contractReference} onChange={(e) => updateIdentityField("contractReference", e.target.value.slice(0, 50))} {...fieldProps} />
                <DropdownList label="Delivery Method" required labelPosition="left" value={identity.deliveryMethod} onChange={(v) => updateIdentityField("deliveryMethod", v)} options={deliveryMethodOptions} {...fieldProps} />
                <DropdownList label="Governance" required labelPosition="left" value={identity.projectGovernance} onChange={(v) => updateIdentityField("projectGovernance", v)} options={projectGovernanceOptions} {...fieldProps} />
                <NumberStepField label="Duration (weeks)" required labelPosition="left" placeholder="Ex: 24" value={durationWeeks} min={1} max={520} step={1} onChange={(v) => updateIdentityField("duration", v)} {...fieldProps} labelWidth={210} width="40%"/>          
              </div>

              {/* (3) LOCATION */}
              <div style={identityCardStyle}>
                <div style={identityCardTitleStyle}>Location</div>
                <DropdownList label="Country" placeholder="Ex: Ghana" required options={countryOptions} value={identity.country} onChange={(v) => updateIdentityField("country", v)} {...fieldProps} />
                <TextField label="City / Region" placeholder="Ex: Wenchi, région de Kara" value={identity.region} onChange={(e) => updateIdentityField("region", e.target.value)} {...fieldProps} />
              </div>

              {/* (3) LOCATION */}
              <div style={identityCardStyle}>
                <div style={identityCardTitleStyle}>Intervention</div>
                <DropdownMulti label="Intervention Nature" required selectedValues={interventionNature} onChange={setInterventionNature} options={interventionNatureOptions} labelWidth={180} marginTop={0} marginBottom={0} width="100%" height={32} gap={10} />             
                <SwitchButton label="Phased works" enabled={phasedExecution} onChange={setPhasedExecution} showText={false} size="small" width="100%" height={32} labelWidth={190} gap={10} marginLeft={10} marginTop={0} marginBottom={6}/>
              </div>
            </div>
          );
        })(),
      },
    ],
  };

  const zoneA2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: [
      {
        icon: "/assets/icones/container/shield.svg",
        title: "Cadre général du projet",
        content: (
          <div style={infoTextStyle}>
            <p style={infoParagraphStyle}>Cet onglet établit le cadre administratif et stratégique du projet. Il constitue la base de référence pour l’ensemble des modules Corelia.</p>
            <ul style={infoListStyle}>
              <li>Définit l’identité officielle et la description du projet.</li>
              <li>Identifie les acteurs clés et les responsabilités principales.</li>
              <li>Précise la localisation et le contexte général d’implantation.</li>
              <li>Fournit un socle stable pour la structuration ultérieure.</li>
            </ul>
          </div>
        ),
      },
    ],
  };

  /* ============================================================================
     ZONES B1 & B2 — GOVERNANCE
  ============================================================================ */
  const zoneB1 = { style: baseLeftZoneStyle, gap: 16, sectionGap: 10, sections: [{ title: "Governance", content: <div style={{ display: "flex", flexDirection: "column", gap: 16 }} /> }] };

  const zoneB2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: [
      {
        icon: "/assets/icones/container/shield.svg",
        title: "Governance — Rôle dans le cadrage",
        content: (
          <div style={infoTextStyle}>
            <p style={infoParagraphStyle}>xxxxx</p>
            <ul style={infoListStyle}>
              <li>
                <strong>xx</strong> aaaaaa
              </li>
            </ul>
          </div>
        ),
      },
    ],
  };

  /* ============================================================================
     ZONES C1 & C2 — TYPOLOGY
  ============================================================================ */
  const zoneC1 = {
    style: baseLeftZoneStyle,
    gap: 12,
    sectionGap: 12,
    sections: [
      {
        content: (
          <div style={{ ...grid2ColsStyle, paddingBottom: 180 }}>
            {/* (1) CLASSIFICATION */}
            <div style={cardStyle}>
              <div style={cardTitleStyle}>Classification</div>
              <RadioGroup label="Sector" required name="sector" value={sector} onChange={handleSectorChange} options={sectorOptions} orientation="horizontal" labelWidth={180} width="100%" />
              <DropdownList label="Typology" required value={typology} onChange={(v) => { setTypology(v); setSubTypology(""); }} options={typologyOptionsFiltered} disabled={!sector} labelWidth={180} marginTop={0} marginBottom={6} {...dropdownBaseProps} />
              <DropdownMulti label="Sub-typology" required selectedValues={subTypology} onChange={setSubTypology} options={subTypologyOptionsFiltered} disabled={!isSubTypologyEnabled} 
                placeholder={!typology ? "Sélectionner une typologie d’abord" : !isSubTypologyEnabled ? "Non applicable pour cette typologie" : "Sélectionner..."} labelWidth={180} width="100%" />              
            </div>

            {/* (4) Engineering Maturity */}
            <div style={cardStyle}>
              <div style={cardTitleStyle}>Engineering Maturity</div>
              <SliderSelector label="Structure granularity" value={designMaturity} onChange={setDesignMaturity} levels={designMaturityOptions} width="720px" labelWidth="220px" longLabelWidth="260px" longLabelOffset={38} style={{ marginTop: 8, gap: 16 }} />
              <SliderSelector label="Interfaces complexity" value={complexityLevel} onChange={setComplexityLevel} levels={COMPLEXITY_LEVEL_OPTIONS} width="720px" labelWidth="220px" longLabelWidth="260px" longLabelOffset={38} style={{ marginTop: 8, gap: 16 }} />      
            </div>

            {/* (3) SITE CONTEXT */}
            <div style={cardStyle}>
              <div style={cardTitleStyle}>Site Context</div>
              <DropdownMulti label="Project Footprint" required selectedValues={projectFootprint} onChange={setProjectFootprint} options={projectFootprintOptions} width="100%" height={32} labelWidth={180} gap={10} marginTop={10} marginBottom={6} />             
              <DropdownMulti label="Site constraints"required selectedValues={siteConstraints} onChange={setSiteConstraints} options={siteConstraintsOptions} width="100%" height={32} labelWidth={180} gap={0} marginTop={0} marginBottom={6} />     
              <RadioGroup label="Operational context" required name="operationalContinuity" value={operationalContinuity} onChange={setOperationalContinuity} options={operationalContinuityOptions} orientation="vertical" align="start" labelWidth={180} width="100%" rowGap={6}/> 
            </div>

            {/* (3) PACKAGES */}
            <div style={cardStyle}>
              <div style={cardTitleStyle}>Packages</div>
              <CheckboxGroup label="Disciplines / Lots" name="disciplines" options={disciplinesCheckboxOptions} onChange={handleDisciplinesChange} orientation="vertical" align="start" checkboxPosition="left" labelWidth={240}  width="100%" rowGap={6} />
            
              </div>
            
          </div>
        ),
      },
    ],
  };

  const zoneC2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: [
      {
        icon: "/assets/icones/container/shield.svg",
        title: "Guidelines structurants",
        content: (
          <div style={infoTextStyle}>
            <p style={infoParagraphStyle}>Cet onglet définit les paramètres structurants du projet. Il qualifie le profil, le contexte d’intervention et les contraintes majeures.</p>
            <ul style={infoListStyle}>
              <li>Les choix réalisés ici déterminent les templates activés dans les modules suivants.</li>
              <li>Ils influencent la structuration Breakdown et Systems.</li>
              <li>
                Le <strong>Project Complexity Level</strong> précise le niveau global de complexité.
              </li>
              <li>
                Les <strong>Project Interfaces (Organizational & Operational)</strong> cadrent les interactions clés du projet.
              </li>
              <li>Ces éléments constituent un cadre stable et transversal.</li>
            </ul>
          </div>
        ),
      },
    ],
  };

  /* ============================================================================
     ZONE D1 — COMPONENTS & CONFIGURATION
  ============================================================================ */
  const zoneD1 = {
  style: baseLeftZoneStyle,
  gap: 12,
  sectionGap: 12,
  sections: [
    {
      content: (
        <>
          <div style={{ ...grid3ColsStyle, paddingBottom: 12 }}>
            {/* (1) MAIN SYSTEMS / FLOWS */}
            <div style={cardRow3Style}>
              <div style={cardTitleStyle}>Main Systems / Flows</div>
              <div style={cardBody3ColsStyle}>
                
                <div style={{ display: "flex", flexDirection: "column", gap: 25 }}>
                <DropdownList label="Organization mode" required name="systemsOrganization" value={systemsOrganization} onChange={setSystemsOrganization} options={systemsOrganizationOptions} align="start" labelWidth={260} width="95%" />
                <DropdownList label="Standardisation approach" required name="modularityApproach" value={modularityApproach} onChange={setModularityApproach} options={modularityApproachOptions} align="start" labelWidth={260} width="95%" />
                </div>
                <CheckboxGroup label="Systems / Flows" name="mainFlows" options={mainFlowsCheckboxOptions} onChange={handleMainFlowsChange} orientation="vertical" align="start" checkboxPosition="left" labelPosition="left" labelWidth={180} width="100%" rowGap={6}/>
                {/* <CheckboxGroup label="Disciplines" name="disciplines" options={disciplinesCheckboxOptions} onChange={handleDisciplinesChange} orientation="vertical" align="start" checkboxPosition="left" labelWidth={120} width="100%" /> */}
                <CheckboxGroup label="External System Connections" name="externalConnections" options={externalConnectionsCheckboxOptions} onChange={handleExternalConnectionsChange} orientation="vertical" align="start" checkboxPosition="left" labelWidth={280} width="100%"rowGap={6} />
              </div>
            </div>
          </div>

          <div style={{ ...grid3ColsStyle, paddingBottom: 180 }}>
            {/* (2) ARCHITECTURE */}
            <div style={cardRow3Style}>
              <div style={cardTitleStyle}>System Strategy</div>
              <div style={cardBody3ColsStyle}>     
                <RadioGroup label="Interconnection topology" required name="interconnectionTopology" value={interconnectionTopology} onChange={setInterconnectionTopology} options={interconnectionTopologyOptions} orientation="vertical" align="start" labelWidth={180} width="100%" rowGap={6}/>            
                <RadioGroup label="Future expansion" required name="expansionStrategy" value={futureExpansionStrategy} onChange={setFutureExpansionStrategy} options={expansionStrategyOptions} orientation="vertical" align="start" labelWidth={220} width="100%" rowGap={6}/>
              </div>
            </div>
          </div>
        </>
      ),
    },
  ],
};














  
  const zoneD2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: [
      {
        icon: "/assets/icones/container/shield.svg",
        title: "Positionnement opérationnel",
        content: (
          <div style={infoTextStyle}>
            <p style={infoParagraphStyle}>Cet onglet formalise la logique globale de fonctionnement du projet avant toute décomposition détaillée.</p>
            <ul style={infoListStyle}>
              <li>
                Le <strong>Project Operating Mode</strong> et la disponibilité attendue définissent le niveau de service.
              </li>
              <li>Le pattern architectural global structure l’organisation des systèmes.</li>
              <li>
                Les <strong>External System Connections</strong> qualifient les interconnexions techniques majeures.
              </li>
              <li>Les niveaux d’automatisation, de criticité et de robustesse orientent la génération des architectures systèmes.</li>
            </ul>
          </div>
        ),
      },
    ],
  };


  

  const zoneE1 = { style: baseLeftZoneStyle, gap: 16, sectionGap: 6, sections: [
      { title: "Zone E1 — Vue générale", content: (
      <div>Aucun élément sélectionné.
        
        <MockEngineViewer
          
          
          typologyId={typologyConfig}
          onTypologyChange={setTypologyConfig}
          startDate={start}
          onStartDateChange={setStart}
        />



      </div> 
      ),

      }
    ] 
  };
  const zoneE2 = { style: baseRightZoneStyle, gap: 12, sectionGap: 6, sections: [{ icon: "/assets/icones/container/shield.svg", title: "Zone E2 — Paramètres", content: <div>Configuration non définie…</div> }] };
  const zoneF1 = { style: baseLeftZoneStyle, gap: 16, sectionGap: 6, sections: [{ title: "Zone F1 — Vue générale", content: <div>Aucun élément sélectionné.</div> }] };
  const zoneF2 = { style: baseRightZoneStyle, gap: 12, sectionGap: 6, sections: [{ icon: "/assets/icones/container/shield.svg", title: "Zone F2 — Paramètres", content: <div>Configuration non définie…</div> }] };

  /* ============================================================================
     TABS
  ============================================================================ */
  const TabA = (
    <div style={tabStyle}>
      <ContainerBase {...zoneA1} />
      {/* <ContainerBase {...zoneA2} /> */}
    </div>
  );

  const TabB = (
    <div style={tabStyle}>
      <ContainerBase {...zoneB1} />
      {/* <ContainerBase {...zoneB2} /> */}
    </div>
  );

  const TabC = (
    <div style={tabStyle}>
      <ContainerBase {...zoneC1} />
      {/* <ContainerBase {...zoneC2} /> */}
    </div>
  );

  const TabD = (
    <div style={tabStyle}>
      <ContainerBase {...zoneD1} />
      {/* <ContainerBase {...zoneD2} /> */}
    </div>
  );

  const TabE = (
    <div style={tabStyle}>
      <ContainerBase {...zoneE1} />
      {/* <ContainerBase {...zoneE2} /> */}
    </div>
  );

  const TabF = (
    <div style={tabStyle}>
      <ContainerBase {...zoneF1} />
      {/* <ContainerBase {...zoneF2} /> */}
    </div>
  );

  const tabs = [
    { label: "Identity", content: TabA },
    { label: "Typology", content: TabC },
    { label: "Components", content: TabD },
    { label: "Interfaces", content: TabB },
    { label: "Analysis", content: TabE },
    // { label: "Objectives and Risks", content: TabF },
  ];

  /* ============================================================================
     RENDER
  ============================================================================ */
  return (
    <div style={{ width: "100%", height: "100%", overflowX: "hidden" }}>
      {/* <ToolbarHeadSubpage
        style={{ padding: 24, height: 50, minHeight: 50, overflow: "visible", background: "transparent" }}
        left={[
          <Button key="new" variant="blue" onClick={() => alert("Template — action fictive")}>
            Nouvel élément
          </Button>,
        ]}
        center={[]}
        right={[
          <IconButton key="corelens" icon="/assets/icones/info.svg" variant="white" size="32" rounded="true" onClick={() => setIsCoreLensOpen((v) => !v)} />,
          <Switch key="switch" label="Actifs" enabled={switchOn} onChange={setSwitchOn} showText={false} size="small" />,
        ]}
      /> */}

      <JourneyTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        componentWidth="50%"
        stepsWidth="100%"
        componentOffsetLeft={0}
        headerPaddingLeft={24}
        tabsDisabled={false}
        containerStyle={{
          height: "calc(100vh - 160px)",
          minHeight: "calc(100vh - 160px)",
          overflow: "hidden",
        }}
      />
    </div>
  );
};

/* ============================================================================
   STYLE COMMUN TABS
============================================================================ */
const tabStyle = { display: "flex", flexDirection: "row", gap: 10, padding: 10, overflowY: "clip", overflowX: "hidden", minWidth: "fit-content", boxSizing: "border-box" };

export default Subpage_101;
